import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "../ui";
import { FormLabel } from "@/components";
import { Loader } from "../Loader";
import { toast } from "@/components/Toast";
import { cn, invoiceActionStatus } from "@/lib/utils";
import { X } from "lucide-react";
import { DatePicker } from "..";
import { useParams } from "react-router-dom";
import moment from "moment";

const ApproveRejectInvoiceLineItemDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  isApproved,
  text,
}: any) => {
  const { invoiceAction } = useParams();
  const [reason, setReason] = useState("");
  const [agmDate, setAGMDate] = useState("");
  const [agmConducted, setAGMConducted] = useState<boolean>(false);
  const [openReasonDialog, setOpenReasonDialog] = useState<boolean>(false);

  const isAGM = invoiceAction == invoiceActionStatus.pending_for_agm_approval;
  const onSubmit = () => {
    if (reason.trim()?.length < 5) {
      toast.error("Please enter a reason with at least 5 characters");
      return;
    } else if (isAGM && !agmDate) {
      toast.error("Please enter AGM Conducted Date");
      return;
    }
    // setReason("");
    onConfirm(
      isAGM
        ? `AGM conducted Date: ${moment(agmDate).format(
            "MM/DD/YYYY"
          )}-${reason}`
        : `${reason}`
    );
  };

  const onNo = () => {
    onClose();
  };

  const onYes = () => {
    setOpenReasonDialog(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        {openReasonDialog ? (
          <DialogContent
            className="max-w-screen-2xl p-6 rounded-2xl shadow-xl"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-primary">
                  {isApproved ? "Reason for Approval" : "Reason for Rejection"}
                  <div className="h-1 w-[60%] bg-primary rounded-full" />
                </DialogTitle>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    // onClick={onCloseClick}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
                  >
                    <X className="h-5 w-5 stroke-[5px]" />
                  </Button>
                </DialogClose>
              </div>
            </DialogHeader>

            <DialogDescription className="text-gray-600 mt-2">
              Are you sure you want to approve this budget? Please provide a
              reason for {isApproved ? "approval" : "rejection"} before
              proceeding.
              <div className="relative flex flex-col items-start gap-1 mt-5">
                <FormLabel>
                  Description<span className="text-red-700">*</span>
                </FormLabel>
                <Textarea
                  onChange={(e) => setReason(e.target.value)}
                  value={reason}
                  rows={10}
                  placeholder="Please enter"
                />
              </div>
              {isApproved && isAGM && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px] items-center">
                  <div className="flex items-center">
                    <Checkbox
                      className="mr-3"
                      onCheckedChange={(checked: boolean) =>
                        setAGMConducted(checked)
                      }
                      checked={agmConducted}
                    />{" "}
                    Is AGM Conducted?
                  </div>
                  <div className="relative flex flex-col items-start gap-1 mt-5">
                    <FormLabel>
                      AGM Conducted Date<span className="text-red-700">*</span>
                    </FormLabel>
                    <DatePicker
                      date={agmDate}
                      setDate={setAGMDate}
                      disabled={!agmConducted}
                    />
                  </div>
                </div>
              )}
            </DialogDescription>

            <div className="flex justify-end gap-4 mt-6">
              <Button
                variant="default"
                className={cn(
                  isApproved
                    ? "bg-green-600 hover:bg-green-600"
                    : "bg-red-600 hover:bg-red-600"
                )}
                onClick={onSubmit}
                disabled={isLoading || (isApproved && isAGM && !agmConducted)}
              >
                {isLoading && <Loader variant="secondary" />}{" "}
                {isApproved ? "Approve" : "Reject"}
              </Button>
            </div>
          </DialogContent>
        ) : (
          <DialogContent
            className="max-w-screen-2xl p-6 rounded-2xl shadow-xl"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-primary">
                  {isApproved
                    ? "Approval Confirmation!"
                    : "Reject Confirmation!"}
                  <div className="h-1 w-[60%] bg-primary rounded-full" />
                </DialogTitle>
                <DialogClose asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    // onClick={onCloseClick}
                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none"
                  >
                    <X className="h-5 w-5 stroke-[5px]" />
                  </Button>
                </DialogClose>
              </div>
            </DialogHeader>

            <DialogDescription className="text-gray-600 mt-2">
              {text}
            </DialogDescription>

            <div className="flex justify-end gap-4 mt-6">
              <Button variant="outline" onClick={onNo} disabled={isLoading}>
                No
              </Button>
              <Button variant="default" onClick={onYes} disabled={isLoading}>
                Yes
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default ApproveRejectInvoiceLineItemDialog;
